import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react';
import type {
  Availability,
  EducationLevel,
  Gender,
  MessageHitDto,
  Nationality,
} from '@chat-emp/shared';
import { ageBounds } from '@chat-emp/shared';
import type { StreamChat } from 'stream-chat';

import { Checkbox } from '../components/Checkbox.js';
import { EmptyState } from '../components/EmptyState.js';
import { LoadingState } from '../components/LoadingState.js';
import { Icon, type IconName } from '../components/Icon.js';
import { Toast, type ToastAction } from '../components/Toast.js';
import {
  useFacetsBy,
  useInboxFacets,
  useInboxList,
  useHeldTotal,
  useInboxRevision,
  FACET_STALE_EVENTS,
  useInboxTotal,
  useMessageHits,
  SCOPE_STALE_EVENTS,
  type UseInboxList,
} from '../data/inbox.js';
import { Marked } from '../components/Marked.js';
import { windowAround } from '../util/snippet.js';
import { useStackedApplications } from '../data/stacks.js';
import type {
  ApplicationStatus,
  ChannelId,
  Form,
  GroupBy,
  InboxItem,
  ParticipantId,
} from '../fixtures/domain.js';
import {
  applicationStatusOf,
  applicationStatusOptions,
  bulkTargets,
  canAssignApplicationStatus,
  isEnded,
  isJobBounded,
  matchesSearch,
} from '../fixtures/rules.js';
import { track } from '../lib/analytics.js';
import { cidParts, emitToHost } from '../lib/bridge.js';
import { endChat, setApplicantStatus } from '../lib/facade.js';
import { compactCount, listSummary, plural } from '../util/format.js';
import { useCanHover } from '../util/useCanHover.js';
import { useDismiss } from '../util/useDismiss.js';
import { sendFormTo, sendMessageTo } from '../data/source.js';
import { AddToListDialog } from './AddToListDialog.js';
import { BulkActionDialog, type BulkDialogAction } from './BulkActionDialog.js';
import { BulkSendDialog } from './BulkSendDialog.js';
import { ChannelPreview } from './ChannelPreview.js';
import { FilterDialog, type NumberRange } from './FilterDialog.js';
import { ListBadge } from './ListBadge.js';
import {
  filterListItems,
  groupListItems,
  heldUnreadCut,
  listCounts,
  listJobTree,
  listLocations,
  type ListGroup,
} from './list-filter.js';
import { BulkActions } from './BulkActions.js';
import { StackedPreview } from './StackedPreview.js';
import { ViewPanel } from './ViewPanel.js';
import {
  foldedExtras,
  groupByPerson,
  stackRows,
  type ApplicationSets,
  type InboxRow,
} from './stack.js';
import { runBulk, type BulkResult } from './bulkSend.js';
import { selectAllNext } from './selection.js';
import { buildJobTree } from './job-tree.js';
import { KindStepper, type InboxView } from './KindStepper.js';
import {
  DIRECT_GROUP_KEY,
  emptyQuery,
  scopedQuery,
  scopeQuery,
  withFilter,
  withoutFilter,
  type InboxQuery,
} from './query.js';
import { useListMembers } from './useListMembers.js';
import { useLists, type ConversationList, type UseLists } from './useLists.js';
import styles from './InboxPanel.module.css';

/** One row of the Messages group: a matching message, the loaded conversation
 *  it belongs to, and its snippet, cut once per result set. */
interface HitRow {
  hit: MessageHitDto;
  item: InboxItem;
  snippet: string;
}

/**
 * What the funnel was set to when a row was opened (AI-2858). Analytics only —
 * nothing renders from it. Absent when the row came from somewhere with no
 * funnel of its own, the Ended and Info rails included.
 */
export interface FunnelContext {
  filter_active: boolean;
  facets_applied: string[];
  /**
   * How many values are ticked in each row. `facets_applied` says the job
   * filter was on; this says whether it was narrowed to one posting or to
   * five, which are different enough acts to be worth telling apart without
   * joining back to the `filter_applied` that set them.
   */
  values_count: Record<string, number>;
  /** How long the list was at that moment. Null while the count is in flight. */
  results_count: number | null;
  /**
   * Whether there was text in the search box when the row was opened (AI-3011).
   * Deliberately wider than "clicked a search result": any chat opened while a
   * search is live counts, because that is what "opened with search on" asks.
   */
  search_active: boolean;
  /** Which of the three lists the row came from. `chats` off a search — the
   *  other two groups only exist while one is running. */
  result_list: ResultList;
}

/** The inbox's three result groups, as the search draws them. */
export type ResultList = 'chats' | 'messages' | 'jobseeker_details';

/** Rows as channels: a fold stands for every application behind it. */
function countChannels(rows: InboxRow[]): number {
  return rows.reduce((n, row) => n + (row.kind === 'stack' ? row.items.length : 1), 0);
}

/** The stepper's view as the conversation kinds it narrows to. */
function kindsFor(view: InboxView): ('job' | 'direct')[] {
  if (view === 'jobs') return ['job'];
  if (view === 'direct') return ['direct'];
  return ['job', 'direct'];
}

/**
 * Whether any conversation this view can hold carries a job, and so whether
 * grouping by job says anything about the list.
 *
 * The read model's facets exclude a NULL group by design ("column not
 * applicable"), so a job-less chat produces no job group. A mixed list still
 * offers the grouping: its remainder renders under `DIRECT_GROUP_KEY` rather
 * than falling out of the list. A list with no job at all offers nothing to
 * group by, so the option is withheld.
 */
export function canGroupByJob(view: InboxView): boolean {
  return kindsFor(view).includes('job');
}

/**
 * The grouping a fresh panel opens on, and the name for what this column has
 * always done: a person's several conversations folded into their row
 * (`stackRows`). It was called None while doing exactly that, which left the
 * control claiming the list was ungrouped and the funnel dot lit for anyone who
 * chose the grouping the panel was already in.
 *
 * One constant behind the initial state, the dot's "anything set?" and the
 * view-switch reset, so the three cannot drift apart.
 */
const DEFAULT_GROUP_BY: GroupBy = 'person';

/** How long the picks must sit still before a burst of ticks counts as one act. */
const PICK_QUIET_MS = 600;

/**
 * The only grouping the read model performs. Person and None are two ways of
 * drawing one ungrouped page — folded and flat — so neither reaches the wire,
 * and asking for person facets would buy sections nothing renders.
 */
function wireGrouping(groupBy: GroupBy): GroupBy {
  return groupBy === 'job' ? 'job' : 'none';
}

/**
 * How a fresh list draws itself: not compact, meaning every row holds its
 * checkbox column open, the same thing touch has always done. Compact is the
 * departure, and picking up a set is common enough here that the boxes are
 * worth their width by default. The baseline the view button's dot is measured
 * against, alongside `DEFAULT_GROUP_BY`.
 */
const DEFAULT_COMPACT = false;

/**
 * Which of the header's two tool popovers is open, if either. One value rather
 * than a flag each: they hang off adjacent buttons and open in the same place,
 * so two open at once is one sheet lying on top of the other.
 */
type Tool = 'view' | 'filter';

/** Folds nothing. What None draws, and what the answer is until it lands. */
const NO_APPLICATIONS: ApplicationSets = new Map();

/** Shared empty, so a closed section reports the same rows every time. */
const NO_ITEMS: InboxItem[] = [];

interface InboxPanelProps {
  /** Connected Stream client; null until the session is up. */
  client: StreamChat | null;
  selectedId: ChannelId | null;
  /** When set, this panel IS a list's detail view: scoped to that list, and
   *  (with onExitList) showing a back header in place of the view switcher. */
  initialListId?: number | null;
  onExitList?: () => void;
  /** Which view the stepper opens on, from `?view=` — the host's deep link into
   *  a kind of conversation (see ChatView). Seeded once at mount, not a
   *  controlled prop: after that the stepper is the employer's to move. */
  initialView?: InboxView;
  onSelect: (item: InboxItem, funnel?: FunnelContext, messageId?: string) => void;
  onNewChat?: () => void;
  /** Go to a saved list, which the "Added to …" toast offers once it has run. */
  onOpenList?: (listId: number) => void;
}

/** How long one term keeps counting as "the same search" for the repeat flag —
 *  mirrors the upstream memo's sliding idle window. */
const SEARCH_REPEAT_WINDOW_MS = 2 * 60_000;
/** When to report a search that never fully settled, so it shows up as a
 *  finding instead of vanishing from the data. */
const SEARCH_TIMING_GIVE_UP_MS = 5_000;

export function InboxPanel({
  client,
  selectedId,
  initialListId,
  onExitList,
  initialView,
  onSelect,
  onNewChat,
  onOpenList,
}: InboxPanelProps) {
  const [view, setView] = useState<InboxView>(initialView ?? 'all');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [unread, setUnread] = useState(false);
  const [jobIds, setJobIds] = useState<string[]>([]);
  // The raw location picks. What actually applies is `locations` below, which
  // is this narrowed to what the current job picks still offer.
  const [locationPicks, setLocationPicks] = useState<string[]>([]);
  /**
   * The candidate rows. Unlike the places these are not pruned to what the
   * facets currently offer: the two closed ladders are validated upstream, and
   * the two open lists are drawn from an index whose coverage is thin enough
   * that "not offered right now" is a poor reason to destroy a pick.
   */
  const [skillPicks, setSkillPicks] = useState<string[]>([]);
  const [languagePicks, setLanguagePicks] = useState<string[]>([]);
  const [educationPicks, setEducationPicks] = useState<EducationLevel[]>([]);
  const [preferredLocationPicks, setPreferredLocationPicks] = useState<string[]>([]);
  const [nationalityPicks, setNationalityPicks] = useState<Nationality[]>([]);
  const [availabilityPicks, setAvailabilityPicks] = useState<Availability[]>([]);
  const [genderPicks, setGenderPicks] = useState<Gender[]>([]);
  const [stagePicks, setStagePicks] = useState<ApplicationStatus[]>([]);
  const [salaryRange, setSalaryRange] = useState<NumberRange>({});
  const [ageRange, setAgeRange] = useState<NumberRange>({});
  const ageCut = useMemo(() => ageBounds(ageRange), [ageRange]);
  const [groupBy, setGroupBy] = useState<GroupBy>(DEFAULT_GROUP_BY);
  // Compact takes the boxes back off the rows, leaving them to the hover
  // reveal. An employer working through a set wants them out; one reading the
  // inbox wants the width back, so it is a setting rather than a rule.
  const [compact, setCompact] = useState(DEFAULT_COMPACT);
  const [tool, setTool] = useState<Tool | null>(null);

  // Bulk selection. Held here so both the flat list and the grouped sections
  // read one source; the count and select-all ride in the header.
  const [selectedIds, setSelectedIds] = useState<ChannelId[]>([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [addingToList, setAddingToList] = useState(false);
  // The hiring-stage / End confirm dialog that's open (from the "…" overflow), or null.
  const [bulkAction, setBulkAction] = useState<BulkDialogAction | null>(null);
  // What the toast says once a filing has run, and what it offers afterwards.
  const [toast, setToast] = useState<ToastState | null>(null);
  // Fixed for the panel's life: a list detail seeds it once and never toggles
  // (the in-row selector is gone — you leave via the back header, not a chip).
  const [activeListId] = useState<number | null>(initialListId ?? null);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  /**
   * The rows each open group section loaded, reported up because only the
   * section queried them (AI-3006). Compared by id rather than by identity: the
   * list hook hands back a fresh object per render, and bailing here is what
   * keeps the report from re-rendering the panel on every one of them.
   */
  const [sectionRows, setSectionRows] = useState<ReadonlyMap<string, InboxItem[]>>(() => new Map());
  const reportSectionRows = useCallback((key: string, items: InboxItem[]) => {
    setSectionRows((current) => {
      const held = current.get(key) ?? NO_ITEMS;
      const same =
        held.length === items.length &&
        held.every((item, i) => item.channel.id === items[i]!.channel.id);
      if (same) return current;
      const next = new Map(current);
      if (items.length === 0) next.delete(key);
      else next.set(key, items);
      return next;
    });
  }, []);
  /** One door for every tick, so a folded row sets its applications as one batch
   *  (R15) and a single row comes through with a one-id batch. */
  const selectMany = (ids: ChannelId[], selected: boolean) => {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      for (const id of ids) {
        if (selected) next.add(id);
        else next.delete(id);
      }
      return [...next];
    });
  };
  const toggleSelect = (id: ChannelId) => selectMany([id], !selectedSet.has(id));
  // The way out of the mode. Every pick, not just the ones on screen: one
  // surviving would come back the next time a filter widened.
  /**
   * Whether this selection was made with Select All rather than row by row
   * (AI-3012). Sticky for the life of the selection: how far "all" reached is
   * already `targeted`, and unticking a row afterwards does not turn a
   * select-all into a hand-picked set.
   *
   * A ref, not state — nothing on screen reads it, and a re-render per press
   * to move a number nobody sees would be a cost for nothing.
   */
  const selectedViaAll = useRef(false);
  const clearSelection = () => {
    selectedViaAll.current = false;
    setSelectedIds([]);
  };

  /**
   * Which folded rows are open, by person.
   *
   * Nothing derives this — see `StackedPreview`. Keyed by person rather than by
   * channel so a reply reordering someone's applications cannot close their row,
   * and held on the panel so scrolling a row out and back does not either.
   */
  const [expandedPeople, setExpandedPeople] = useState<ParticipantId[]>([]);
  const expandedSet = useMemo(() => new Set(expandedPeople), [expandedPeople]);
  const toggleExpand = (id: ParticipantId) =>
    setExpandedPeople((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  // One dismiss zone around both buttons and both sheets: a press on the other
  // button is inside it, and that button closes what is open by opening its own.
  const toolsRef = useRef<HTMLDivElement>(null);
  useDismiss(tool !== null, () => closeTool(), toolsRef);

  // Typing must not fire a query per keystroke: the read model is a network hop
  // away, and prefix search only means anything after a few characters.
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const groupByJobOffered = canGroupByJob(view);

  /**
   * Everything the inbox is narrowed by except the location picks and the scope
   * pill: the job and place chips are faceted over exactly this query, and a
   * chip that vanished on being chosen would leave no way to put it back.
   */
  const baseQuery = useMemo(() => {
    // The Chats section is open conversations; ended ones have their own rail.
    let q = withFilter(emptyQuery(), { kind: 'status', phases: ['open'] });
    q = withFilter(q, { kind: 'kind', kinds: kindsFor(view) });
    if (debounced.trim()) q = withFilter(q, { kind: 'search', text: debounced, field: 'any' });
    if (jobIds.length > 0) q = withFilter(q, { kind: 'job', jobIds });
    return { ...q, grouping: { by: wireGrouping(groupBy) } };
  }, [view, debounced, jobIds, groupBy]);

  const locationFacetQuery = useMemo(
    () => ({ ...baseQuery, grouping: { by: 'location' as GroupBy } }),
    [baseQuery],
  );
  /**
   * One cue for every aggregate on this panel. The facets are queried over the
   * whole inbox rather than the rows on screen, so nothing else refreshes them
   * when a conversation arrives or ends — a chat on a job the filter had never
   * listed would leave that job missing from it.
   */
  // Reads (and `message.new`) join the cue only under the Unread scope, where
  // every aggregate below is computed over the cut those events move; on the
  // default scope they change no facet and stay out of the set (AI-2956).
  const revision = useInboxRevision(client, unread ? SCOPE_STALE_EVENTS : FACET_STALE_EVENTS);
  // Also on `message.new`: a message into a chat already read makes it unread
  // again. The Unread count is off in a list detail and members resolve to
  // nothing outside one, so the two never listen at once and share this set.
  const scopeRevision = useInboxRevision(client, SCOPE_STALE_EVENTS);

  // Fetched for every view now, because the panel reads the options to decide
  // what to render — gating them on a view would make that decision circular.
  // Still held out of a saved list, whose funnel is built from its own members.
  const locationFacets = useInboxFacets(locationFacetQuery, onExitList == null, revision);

  /**
   * The places actually in force. Changing the job picks reshapes what is on
   * offer, and a pick that fell off it would keep narrowing the list with no
   * chip left on screen to explain why — so the offer decides. Derived rather
   * than pruned into state, so a place is not destroyed by a job toggle:
   * unchecking a job and checking it back restores what was picked.
   */
  const locations = useMemo(() => {
    // A saved list filters locations client-side (listSelectedLocs → filterListItems
    // below), so its picks stay out of the server query — routed through here they
    // would narrow the whole-inbox fetch and could page the list's own members off.
    // Job picks are dropped on a view switch for the same reason (see changeView).
    if (onExitList != null) return [];
    // An empty offer is "not loaded yet", not "nowhere to filter by": `loading`
    // is false on the first render too, and pruning against nothing there would
    // drop the picks before the facets ever arrived — one flash of an
    // unfiltered list, and a wasted query, on every apply.
    const known = !locationFacets.loading && locationFacets.facets.length > 0;
    if (!known) return locationPicks;
    const offered = new Set(locationFacets.facets.map((f) => f.key));
    return locationPicks.filter((key) => offered.has(key));
  }, [onExitList, locationFacets.loading, locationFacets.facets, locationPicks]);

  /**
   * The candidate clauses, applied on top of the conversation ones.
   *
   * Each is its own kind, so `withFilter`'s replace-by-kind leaves the others
   * alone — folded into one clause, clearing Skills would take Education with
   * it. An empty row adds nothing, which is what keeps an untouched row from
   * narrowing anything.
   */
  const withCandidates = useCallback(
    (q: InboxQuery): InboxQuery => {
      let next = q;
      if (skillPicks.length > 0) next = withFilter(next, { kind: 'skills', skills: skillPicks });
      if (languagePicks.length > 0) {
        next = withFilter(next, { kind: 'languages', languages: languagePicks });
      }
      if (educationPicks.length > 0) {
        next = withFilter(next, { kind: 'education', levels: educationPicks });
      }
      if (preferredLocationPicks.length > 0) {
        next = withFilter(next, {
          kind: 'preferredLocation',
          locations: preferredLocationPicks,
        });
      }
      if (nationalityPicks.length > 0) {
        next = withFilter(next, { kind: 'nationality', nationalities: nationalityPicks });
      }
      if (availabilityPicks.length > 0) {
        next = withFilter(next, { kind: 'availability', availabilities: availabilityPicks });
      }
      if (genderPicks.length > 0) {
        next = withFilter(next, { kind: 'gender', genders: genderPicks });
      }
      if (stagePicks.length > 0) {
        next = withFilter(next, { kind: 'applicationStatus', stages: stagePicks });
      }
      if (salaryRange.min !== undefined || salaryRange.max !== undefined) {
        next = withFilter(next, { kind: 'expectedSalary', ...salaryRange });
      }
      if (ageCut.min !== undefined || ageCut.max !== undefined) {
        next = withFilter(next, { kind: 'age', ...ageCut });
      }
      return next;
    },
    [
      skillPicks,
      languagePicks,
      educationPicks,
      preferredLocationPicks,
      nationalityPicks,
      availabilityPicks,
      genderPicks,
      stagePicks,
      salaryRange,
      ageCut,
    ],
  );

  const query = useMemo(() => {
    const placed =
      locations.length > 0 ? withFilter(baseQuery, { kind: 'jobLocation', locations }) : baseQuery;
    const narrowed = withCandidates(placed);
    return unread ? scopeQuery(narrowed, 'unread') : narrowed;
  }, [baseQuery, locations, unread, withCandidates]);

  /**
   * A candidate row's options, counted over everything in force *except* that
   * row. Ticking a skill would otherwise reshuffle the skill list under the
   * cursor — every other skill's count becomes its count *within* the one just
   * picked, and the row the employer is reading rearranges as they read it.
   *
   * Held out of a saved list for the same reason the places are: its funnel is
   * built from its own members, and a whole-inbox facet query would offer
   * values none of them have.
   *
   * And asked only while the funnel sheet is open: these options draw nothing
   * else, and each is a candidate-set resolve upstream — the most expensive
   * ask on the panel, re-issued on every revision tick for rows nobody could
   * see (AI-2956).
   */
  const candidateFacetsOn = onExitList == null && tool === 'filter';
  const skillQuery = useMemo(() => withoutFilter(query, 'skills'), [query]);
  const languageQuery = useMemo(() => withoutFilter(query, 'languages'), [query]);
  const educationQuery = useMemo(() => withoutFilter(query, 'education'), [query]);
  const preferredLocationQuery = useMemo(() => withoutFilter(query, 'preferredLocation'), [query]);
  const nationalityQuery = useMemo(() => withoutFilter(query, 'nationality'), [query]);
  const availabilityQuery = useMemo(() => withoutFilter(query, 'availability'), [query]);
  const genderQuery = useMemo(() => withoutFilter(query, 'gender'), [query]);
  const stageQuery = useMemo(() => withoutFilter(query, 'applicationStatus'), [query]);

  const skillFacets = useFacetsBy('skills', skillQuery, candidateFacetsOn, revision);
  const languageFacets = useFacetsBy('languages', languageQuery, candidateFacetsOn, revision);
  const educationFacets = useFacetsBy('education', educationQuery, candidateFacetsOn, revision);
  const preferredLocationFacets = useFacetsBy(
    'preferred_location',
    preferredLocationQuery,
    candidateFacetsOn,
    revision,
  );
  const nationalityFacets = useFacetsBy(
    'nationality',
    nationalityQuery,
    candidateFacetsOn,
    revision,
  );
  const availabilityFacets = useFacetsBy(
    'availability',
    availabilityQuery,
    candidateFacetsOn,
    revision,
  );
  const genderFacets = useFacetsBy('gender', genderQuery, candidateFacetsOn, revision);
  const stageFacets = useFacetsBy('application_status', stageQuery, candidateFacetsOn, revision);
  const stageOptions = useMemo(
    () => applicationStatusOptions(stageFacets.facets),
    [stageFacets.facets],
  );

  const list = useInboxList(query, client);
  const grouped = query.grouping.by !== 'none';

  /**
   * The messages behind the search, joined to the rows that can render them.
   *
   * The list beside these is already the combined answer (the read model ORs
   * the name match with Stream's message match), which is exactly why it reads
   * as a guess: a chat matched on something said weeks ago shows nothing the
   * employer typed. These rows are the explanation — one per matching message,
   * with the words picked out. A hit whose channel is not in the loaded page
   * is dropped rather than drawn nameless.
   */
  const { hits, term: hitTerm } = useMessageHits(activeListId === null ? debounced : '');
  /**
   * Which message row was opened, if any. Keyed by message rather than by
   * channel: several hits can share one chat, and keyed on the channel all of
   * that chat's rows would light at once. Only trusted while its row is still
   * on screen (see `openedRow`), so a stale value cannot strand the mark.
   */
  const [openedHit, setOpenedHit] = useState<string | null>(null);
  const hitRows = useMemo(() => {
    const byChannel = new Map(
      list.items.map((item) => [cidParts(item.channel.id).channelId, item]),
    );
    const rows: HitRow[] = [];
    for (const hit of hits) {
      const item = byChannel.get(hit.channelId);
      // The snippet is cut here rather than in the row: once per result set,
      // not once per row on every keystroke's render.
      if (item) rows.push({ hit, item, snippet: windowAround(hit.text, hitTerm) });
    }
    return rows;
  }, [hits, list.items, hitTerm]);
  const openedRow = hitRows.some((row) => row.hit.messageId === openedHit) ? openedHit : null;

  // A saved list's members resolve directly against Stream rather than filtering
  // the loaded inbox page: a member below that page would never render, so it
  // could not be selected or removed and the count would read "0 of N".
  const { lists, addTo, removeFrom } = useLists();
  const activeList = activeListId ? (lists.find((l) => l.id === activeListId) ?? null) : null;
  /** The mode whose rows come from the sections' own queries, not this panel's
   *  flat page. A saved list groups its loaded members client-side instead. */
  const sectioned = grouped && !activeList;
  // An exit callback is only passed when the panel is a list's detail view.
  const inListDetail = onExitList != null;
  // Members resolve with `watch: false`, so nothing about them changes on its own,
  // and the pill cuts on `unreadCount`, which a plain read moves. Without a
  // revision the detail sits on whatever was true when it opened.
  const listMembers = useListMembers(client, activeList?.channelIds ?? [], scopeRevision);
  // Intersect the resolved members with the LIVE stored ids so a remove drops the
  // row instantly: the store shrinks channelIds synchronously, but the Stream
  // re-query only catches up a round-trip later. Search then narrows what remains
  // (client-side, the same fields the inbox search matches — the list is a
  // hand-picked set, not a server query).
  const activeChannelSet = new Set(activeList?.channelIds ?? []);
  const searchedMembers =
    inListDetail && debounced.trim()
      ? listMembers.items.filter((item) =>
          matchesSearch(item.channel, item.counterpart.name, debounced),
        )
      : listMembers.items;
  // Intersect with the live stored ids so a remove drops the row at once (the store
  // shrinks channelIds synchronously; the Stream re-query lags a round-trip). The
  // funnel's job/place options are built from this set, so the Unread pill is
  // NOT applied here — it narrows the rendered rows below, keeping it orthogonal to
  // the funnel (otherwise pressing it would drop a job/place from the filter offer).
  const memberItems = searchedMembers.filter((item) => activeChannelSet.has(item.channel.id));
  const baseScoped = activeList
    ? {
        ...list,
        items: memberItems,
        hasMore: false,
        loading: listMembers.loading,
        // Only the members query drives the detail's error/retry; a stale set stays
        // on screen when a re-query blips, so the error screen shows only on a first
        // load that resolved nothing.
        error: listMembers.error && listMembers.items.length === 0,
        reload: listMembers.reload,
      }
    : list;

  // In-list funnel: a list is a hand-picked set held client-side, so its filter,
  // grouping and options all run over the loaded members here — no read-model
  // round trip (the inbox's own funnel is server-side and gated to the Job view).
  // Options come from the members, so it only offers a job/place the list holds.
  const listJobs = inListDetail ? listJobTree(baseScoped.items) : [];
  const listLocs = inListDetail ? listLocations(baseScoped.items, jobIds) : [];
  // A location pick the current job picks have narrowed off the offer stops
  // applying but isn't dropped from state — re-checking the job brings it back.
  const listSelectedLocs = locationPicks.filter((key) => listLocs.some((l) => l.key === key));
  // Funnel first (its options came from the un-narrowed members above), then the
  // Unread pill narrows only the rows that render — so the pill can't shrink
  // the filter offer. The detail renders these members directly, so the pill is
  // applied here; the query-driven inbox never sees them.
  //
  // Held apart from the pill's cut because the pills count both: the funnelled
  // set is "All", and its unread subset is the other.
  const listFunnelled = inListDetail
    ? filterListItems(baseScoped.items, jobIds, listSelectedLocs)
    : [];
  /**
   * The detail's held rows — `rowFate`'s `settled`, for a list that has no
   * cursor to hold.
   *
   * Now that the members refresh above, a read really does take a member out of
   * the cut, so the pill would delete the row under the cursor: the conversation
   * opens in the pane and the list beside it goes empty. The inbox answered this
   * by holding a row until the cut is asked again; a list's cut is asked when the
   * pill is pressed or the funnel moves, which is exactly this key.
   *
   * Only the rows are held. `listCounts` still counts live, so the number drops
   * the moment the chat is read — the same one-directional trade #348 made, and
   * for the same reason: a count is a claim about now, a list is what you were
   * given to work through.
   */
  const listCutKey = `${activeList?.id ?? ''}|${unread}|${jobIds.join(',')}|${listSelectedLocs.join(',')}|${debounced.trim()}`;
  const listHeld = useRef<{ key: string; ids: Set<string> }>({ key: '', ids: new Set() });
  if (listHeld.current.key !== listCutKey) listHeld.current = { key: listCutKey, ids: new Set() };
  const scopedList = inListDetail
    ? { ...baseScoped, items: heldUnreadCut(listFunnelled, unread, listHeld.current.ids) }
    : baseScoped;

  /**
   * Every application each person on screen holds — what turns three rows with
   * the same face into one. See `stackRows`.
   *
   * Two sources, because the two lists know different amounts. The inbox is
   * paged, so it has to ask the read model (`useStackedApplications`). A saved
   * list's detail resolves its whole membership up front, so the answer is
   * already in its hands and a round trip would only re-fetch it.
   */
  const inboxApplications = useStackedApplications(
    query,
    list.items,
    // A null client is what keeps the question from being asked at all. Not just
    // the saved list, which already holds the answer, but any view that does not
    // fold: Job's sections render plain rows (R4) and None asks for none, so the
    // answer would be bought and thrown away on every regroup.
    inListDetail || groupBy !== 'person' ? null : client,
    revision,
  );
  const listApplications = useMemo(
    () => (inListDetail ? groupByPerson(scopedList.items) : null),
    [inListDetail, scopedList.items],
  );
  const applications = listApplications ?? inboxApplications;

  /**
   * The applications a folded row shows that the loaded pages do not hold.
   *
   * They are on screen — named on a chip, or counted in the "+n" — so select-all
   * and the bulk actions have to be able to reach them. Without this a row whose
   * box says "all four picked" would hand two to the action that ran.
   *
   * Only for rows that actually fold: a person with one application contributes
   * nothing here, whatever the membership answer holds.
   */
  const stackExtras = useMemo(
    () => foldedExtras(scopedList.items, applications),
    [scopedList.items, applications],
  );

  /** What the employer can actually see: the loaded rows, folded members and all. */
  /**
   * What the employer can actually see (AI-3006).
   *
   * A grouped inbox renders each open section's OWN query, not this panel's flat
   * page, so the two sources cannot be mixed: whichever one is on screen is the
   * whole answer. Everything about a selection reads this, so a row the panel
   * cannot see here is one no bulk action can reach.
   */
  const onScreenItems = useMemo(() => {
    if (!sectioned) {
      return stackExtras.length > 0 ? [...scopedList.items, ...stackExtras] : scopedList.items;
    }
    // Messages rows sit beside the sections while searching and come off the flat
    // page, so one chat can arrive twice. One row, one vote.
    const seen = new Set<ChannelId>();
    const rows: InboxItem[] = [];
    for (const section of sectionRows.values()) {
      for (const item of section) {
        if (seen.has(item.channel.id)) continue;
        seen.add(item.channel.id);
        rows.push(item);
      }
    }
    for (const { item } of hitRows) {
      if (seen.has(item.channel.id)) continue;
      seen.add(item.channel.id);
      rows.push(item);
    }
    return rows;
  }, [sectioned, scopedList.items, stackExtras, sectionRows, hitRows]);

  // Select-all and the count read the loaded flat list. ponytail: in a grouped
  // view this covers the panel's flat query, not each lazily-opened section.
  const visibleIds = onScreenItems.map((item) => item.channel.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
  const toggleSelectAll = () => {
    // The press that clears is the opposite act, and leaves nothing selected
    // for the flag to describe.
    selectedViaAll.current = !allVisibleSelected;
    setSelectedIds(selectAllNext(selectedIds, visibleIds, allVisibleSelected));
  };

  // The picked conversations that are actually on screen. A filter (or leaving a
  // saved list) can hide a picked row, and no bulk action may reach a chat the
  // employer can no longer see — least of all End, which is irreversible. So the
  // actions read the selection intersected with the visible set (scopedList.items,
  // post in-list filter); selectedIds stays the raw store that toggling and
  // select-all write, and what the header counts. Matches the mock's
  // `selectedIds.filter(onScreen)`.
  // ponytail: a row picked inside a lazily-opened group section of the inbox's Job
  // view isn't in scopedList.items either, so it drops from the action too — a
  // fail-safe under-reach (nothing acted on unseen), never an over-reach.
  const selectedItems = onScreenItems.filter((item) => selectedSet.has(item.channel.id));
  const selectedOnScreenIds = selectedItems.map((item) => item.channel.id);
  /**
   * SELECTION MODE: one tick and the header does a different job. The title
   * becomes the count, the All pill becomes select-all, the tools become the
   * actions. Read off the on-screen picks, so the header's count, the box's state
   * and what the actions reach are one number.
   */
  const selecting = selectedItems.length > 0;

  // The mode can arrive without a tick, when a filter widens back onto a pick,
  // and a sheet hanging off a button that has gone points at nothing.
  useEffect(() => {
    if (selecting) closeTool();
  }, [selecting]);

  // A saved list groups its loaded members itself. Only Job sections them:
  // Person folds them in place, the same as the inbox's own column.
  const listGroups: ListGroup[] | null =
    inListDetail && groupBy === 'job' ? groupListItems(scopedList.items, 'job') : null;

  /**
   * The flat column, folded (R4).
   *
   * Only here. Grouping by job splits a person's chats across sections by
   * definition, and grouping by person already puts the same set under a heading
   * with their name on it — so `listGroups` and the inbox's own server-grouped
   * sections render plain rows, and this covers the ungrouped inbox and an
   * ungrouped saved list, which is where the duplicate rows were invisible.
   */
  const clientGrouped = listGroups !== null;
  const allRows: InboxRow[] = useMemo(
    () =>
      clientGrouped
        ? []
        : stackRows(scopedList.items, groupBy === 'person' ? applications : NO_APPLICATIONS),
    [clientGrouped, groupBy, scopedList.items, applications],
  );

  /**
   * The Jobseeker Details split (AI-2946). A profile match is invisible on its row —
   * the fact that matched lives in the info panel — so those chats sit under
   * their own header, the same way the Chats/Messages split explains itself.
   *
   * Precedence keeps every row single: a chat whose NAME also matches stays in
   * Chats (something visible explains it there), so Jobseeker Details holds only the
   * rows nothing on screen accounts for. A stack takes its person's verdict —
   * the fold is by person, and the profile is theirs. The membership comes
   * from the query response and is memo-stable across pages, so the section
   * cannot shift under a cursor.
   */
  const searchingInbox = !inListDetail && debounced.trim() !== '';
  const [rows, candidateRows] = useMemo((): [InboxRow[], InboxRow[]] => {
    if (!searchingInbox || list.profileMatched.size === 0) return [allRows, []];
    const needle = debounced.trim().toLowerCase();
    const profileOnly = (who: { id: ParticipantId; name: string }) =>
      list.profileMatched.has(who.id) && !who.name.toLowerCase().includes(needle);
    const chats: InboxRow[] = [];
    const candidates: InboxRow[] = [];
    for (const row of allRows) {
      const who = row.kind === 'stack' ? row.counterpart : row.item.counterpart;
      (profileOnly(who) ? candidates : chats).push(row);
    }
    return [chats, candidates];
  }, [searchingInbox, list.profileMatched, allRows, debounced]);
  /** Which channels the Jobseeker Details group holds, for `result_list`. */
  const candidateChannelIds = useMemo(() => {
    const ids = new Set<ChannelId>();
    for (const row of candidateRows) {
      if (row.kind === 'stack') for (const item of row.items) ids.add(item.channel.id);
      else ids.add(row.item.channel.id);
    }
    return ids;
  }, [candidateRows]);

  // Which bulk action each on-screen pick can take (see `bulkTargets`): the "…"
  // overflow greys a stage/End row whose subset is empty, and each dialog runs
  // over its action's subset.
  const targets = bulkTargets(selectedItems.map((item) => item.channel));
  const channelById = new Map(selectedItems.map((item) => [item.channel.id, item.channel]));
  // End rejects only the job-bounded chats, and only where a rejection can still
  // be assigned — the same per-chat split the single-chat "End & reject" makes.
  const willReject = (id: ChannelId): boolean => {
    const channel = channelById.get(id);
    return (
      channel != null &&
      isJobBounded(channel) &&
      canAssignApplicationStatus(applicationStatusOf(channel), 'rejected')
    );
  };
  // One derivation for both readers: the overflow row names the ending off it,
  // the dialog counts what the ending costs off it.
  const rejectReach = targets.end.filter(willReject).length;

  /**
   * Run a hiring stage or End over the picked set, here (InboxPanel owns the
   * client + selection). One analytics event per batch — never per chat; the
   * selection clears on the dialog's close, like bulk send.
   */
  const runBulkAction = async (
    action: BulkDialogAction,
    text: string,
    onProgress: (done: number, total: number) => void,
  ): Promise<BulkResult<ChannelId>> => {
    const ids = targets[action];
    const targeted = ids.length;
    // Read with `targeted`, not at the track calls below: the dialog's close
    // clears the selection, and a slow batch can resolve after it.
    const selectAll = selectedViaAll.current;
    if (action === 'end') {
      // End, then reject the job-bounded ones — the same coupling the single-chat
      // flow makes (see source.ts `endWithMessage`). The backend keeps end-chat
      // and applicant-status as separate endpoints, so `alsoRejected` on end-chat
      // does NOT move the applicant; the second call is what rejects. End first,
      // so a failed reject never leaves an applicant rejected on a still-open chat.
      //
      // The end is the unit of done/failed: once endChat resolves the chat is
      // closed for good, so a reject that throws afterwards must NOT bucket it as
      // "couldn't be done" — an ended chat is read-only, so the retry the result
      // offers would be impossible. Swallow the reject failure so the end still
      // counts, and record how many rejects didn't land. ponytail: a dropped
      // reject isn't surfaced in the dialog (rare — applicant-status down while
      // end-chat is up); it stays retriable from the applicant's own thread.
      // Counted from what actually happened, not intent: a reject lands only
      // after its end resolved, so `rejected` can never exceed `ended`. A chat
      // whose end throws increments neither (it's in result.failed).
      let rejected = 0;
      let rejectFailed = 0;
      const result = await runBulk(
        ids,
        async (id) => {
          const reject = willReject(id);
          await endChat(id, text, reject);
          if (reject) {
            try {
              await setApplicantStatus(id, 'rejected');
              rejected += 1;
            } catch {
              rejectFailed += 1;
            }
          }
        },
        onProgress,
      );
      track('bulk_chat_ended', {
        targeted,
        select_all: selectAll,
        rejected,
        rejectFailed,
        ended: result.done,
        failed: result.failed.length,
      });
      return result;
    }
    const result = await runBulk(ids, (id) => setApplicantStatus(id, action), onProgress);
    track('bulk_stage_set', {
      stage: action,
      targeted,
      select_all: selectAll,
      done: result.done,
      failed: result.failed.length,
    });
    return result;
  };

  /**
   * Take the picked chats out of the list. No dialog, no warning: this reaches
   * nobody and ends nothing — it undoes filing, and the toast carries the Undo
   * that redoes it. Snapshotted before the clear, because the Undo re-files this
   * exact set.
   */
  const handleRemoveFromList = () => {
    if (!activeList) return;
    const removed = [...selectedOnScreenIds];
    removeFrom(activeList.id, removed);
    clearSelection();
    setToast(removedReport(activeList, removed, addTo));
  };

  // Send happens here (InboxPanel owns the client + selection). One analytics
  // event for the batch — never per message; the selection clears on close.
  const handleBulkSend = async (
    text: string,
    form: Form | null,
    onProgress: (done: number, total: number) => void,
  ) => {
    const targeted = selectedOnScreenIds.length;
    const selectAll = selectedViaAll.current;
    // With a form, each item is its own dispatch → post → link — a dispatch is
    // per-channel, so there is no batching it above the loop.
    const result = await runBulk(
      selectedOnScreenIds,
      (id) => (form ? sendFormTo(id, text, form) : sendMessageTo(id, text)),
      onProgress,
    );
    track('bulk_message_sent', {
      targeted,
      select_all: selectAll,
      sent: result.done,
      failed: result.failed.length,
      form: form !== null,
    });
    return result;
  };
  const facets = useInboxFacets(query, grouped, revision);

  /**
   * The job filter's options and counts come from the read model, so they cover
   * the whole inbox rather than the page in hand. Leave-one-out over everything
   * in force, as the candidate rows below are: without the job clause, because a
   * filter listing only what you already picked could never be widened — but
   * with every other row, or a job keeps its unfiltered number while the list
   * beside it shrinks, and the panel and the group headers disagree on screen.
   *
   * The scope pill stays out. It is not a row of this panel, and faceting the
   * offer over the pressed cut prunes a pick off it — the pick then goes on
   * narrowing the list from behind a row no longer on screen.
   */
  const jobFacetQuery = useMemo(
    () => ({
      ...withoutFilter(withoutFilter(query, 'job'), 'scope'),
      grouping: { by: 'job' as GroupBy },
    }),
    [query],
  );
  const jobFacets = useInboxFacets(jobFacetQuery, !inListDetail, revision);

  /**
   * Job facets label themselves and name their family: the read model stores
   * the job title and the parent job beside the id, so a facet arrives ready to
   * render. A row the backend's reindex has not reached yet — or one whose
   * posting expired before it ran — has no parent and renders flat, which is
   * exactly the list this showed before families existed.
   */
  const jobTree = useMemo(() => buildJobTree(jobFacets.facets), [jobFacets.facets]);

  /**
   * The job-less rows of a job-grouped list, counted so they can be given a
   * header of their own. Asked only where they can exist: a list of one kind
   * has no remainder, and a saved list groups its loaded members client-side.
   */
  const remainderQuery = useMemo(
    () =>
      withFilter(
        { ...query, grouping: { by: 'none' as GroupBy } },
        { kind: 'kind', kinds: ['direct'] },
      ),
    [query],
  );
  const remainderApplies =
    !inListDetail && query.grouping.by === 'job' && kindsFor(view).includes('direct');
  const remainderCount = useInboxTotal(remainderQuery, remainderApplies, revision);

  /**
   * The remainder held across a re-count, unlike the pills beside it.
   *
   * The two read the same hook and want opposite things from it. A pill shows a
   * number, so a stale one is a lie and blanking is right. This decides whether
   * a *section* exists, and `InboxRows` renders grouped mode from these facets
   * alone — so blanking here does not blank a number, it takes every direct
   * conversation off the list until the new count lands, on every keystroke and
   * chip toggle. A row that vanishes is worse than a count one round trip old,
   * and the job sections beside it are already stale on exactly those terms:
   * `useInboxFacets` keeps its previous facets until the new ones arrive.
   *
   * Cleared when the remainder stops applying, so a section cannot outlive the
   * grouping or the view that produced it.
   */
  const directRemainder = useHeldTotal(remainderCount, remainderApplies);

  // Both counts come from the server alone. Sourcing either from `list.total`
  // flashes the other pill's number on a press, and subtracting the list's held
  // rows double-counts a read once the recount lands.
  const inboxCounts = {
    all: useInboxTotal(scopeQuery(query, 'all'), !inListDetail, revision),
    unread: useInboxTotal(scopeQuery(query, 'unread'), !inListDetail, scopeRevision),
  };

  /**
   * A saved list counts what it holds instead of asking the read model. Its
   * members are resolved directly against Stream and never go through the inbox
   * query, so `total` says nothing about them — and unlike the paginated inbox,
   * a list really is fully loaded, so its length is the honest answer.
   */
  const counts = inListDetail
    ? baseScoped.loading
      ? { all: null, unread: null }
      : listCounts(listFunnelled)
    : inboxCounts;

  /**
   * The last count that landed, held through the next one's round trip — the
   * same trick the rows use, so a report of "how long the list was" matches the
   * list that was on screen rather than the blank the recount leaves.
   */
  const heldAll = useHeldTotal(counts.all, true);

  /** Sections read like the filter list: same labels, same alphabetical order. */
  const groupFacets = useMemo(() => {
    if (query.grouping.by !== 'job') return facets.facets;
    const byTitle = [...facets.facets].sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
    // Last, and only when there is something in it: a remainder is not a job,
    // and an empty header would claim conversations the list does not hold.
    if (!directRemainder) return byTitle;
    return [
      ...byTitle,
      { key: DIRECT_GROUP_KEY, label: 'Direct messages', count: directRemainder },
    ];
  }, [facets.facets, query.grouping.by, directRemainder]);

  // stream-chat parity: emit `channel-search` once the query settles, reporting
  // the count actually shown. Read via a ref so the debounce keys only on the
  // search text, not on unrelated list changes.
  const countRef = useRef(list.total);
  countRef.current = list.total;
  useEffect(() => {
    const q = debounced.trim();
    if (q === '') return;
    const timer = setTimeout(() => {
      emitToHost('channel-search', { query: q, resultsCount: countRef.current });
    }, 300);
    return () => clearTimeout(timer);
  }, [debounced]);

  /**
   * Search timing (AI-2988): what the employer actually waits, measured from
   * the debounce settling to each group's data arriving. The speed decisions
   * queued behind this (progressive resolution first among them) are to be
   * made from these numbers, not estimates.
   *
   * `ms_list` covers the chat rows AND the Jobseeker Details split — both
   * arrive on the same query answer. `ms_hits` is the Messages group.
   * Stamps are taken when an effect observes the settle, so they include
   * React's own commit latency — which is what the employer experiences.
   *
   * `repeat` approximates the upstream memo from this side of the wire: the
   * same term searched in this session within the memo's sliding window. An
   * approximation is enough — its job is separating warm answers from cold
   * in the percentiles, not billing.
   *
   * The term itself never travels: durations, counts and flags only.
   */
  const hitTermRef = useRef(hitTerm);
  hitTermRef.current = hitTerm;
  const searchTiming = useRef<{
    startedAt: number;
    term: string;
    repeat: boolean;
    /** The reload this term triggered has been seen running. Without this,
     *  the settle effect's first pass — same commit, loading still reading
     *  false — would stamp a zero that measures nothing. */
    sawLoading: boolean;
    listMs: number | null;
    hitsMs: number | null;
    reported: boolean;
  } | null>(null);
  const recentTerms = useRef(new Map<string, number>());
  const reportSearchTiming = (settled: boolean) => {
    const t = searchTiming.current;
    if (!t || t.reported) return;
    if (settled && (t.listMs === null || t.hitsMs === null)) return;
    t.reported = true;
    track('channel_searched', {
      results_count: countRef.current,
      settled,
      repeat: t.repeat,
      ...(t.listMs === null ? {} : { ms_list: t.listMs }),
      ...(t.hitsMs === null ? {} : { ms_hits: t.hitsMs }),
    });
  };
  // Keyed on the trimmed term, not the raw input: the compiled filter trims
  // too, so a whitespace-only edit reloads nothing — a timing restarted for
  // one would never settle and would report a phantom give-up.
  const searchTerm = debounced.trim();
  useEffect(() => {
    const term = searchTerm;
    if (term === '' || inListDetail) {
      searchTiming.current = null;
      return;
    }
    const now = Date.now();
    for (const [key, at] of recentTerms.current) {
      if (now - at > SEARCH_REPEAT_WINDOW_MS) recentTerms.current.delete(key);
    }
    const last = recentTerms.current.get(term);
    recentTerms.current.set(term, now);
    searchTiming.current = {
      startedAt: now,
      term,
      repeat: last !== undefined,
      sawLoading: false,
      listMs: null,
      hitsMs: null,
      reported: false,
    };
    // The hits may already answer this term (a repeat the hook kept warm).
    if (hitTermRef.current === term) {
      searchTiming.current.hitsMs = 0;
    }
    // A search that never settles is a finding, not a hole in the data.
    const giveUp = setTimeout(() => reportSearchTiming(false), SEARCH_TIMING_GIVE_UP_MS);
    return () => clearTimeout(giveUp);
  }, [searchTerm, inListDetail]);
  useEffect(() => {
    const t = searchTiming.current;
    if (!t) return;
    if (list.loading) {
      t.sawLoading = true;
      return;
    }
    if (t.sawLoading && t.listMs === null) t.listMs = Date.now() - t.startedAt;
    reportSearchTiming(true);
  }, [list.loading, searchTerm]);
  useEffect(() => {
    const t = searchTiming.current;
    if (!t) return;
    if (t.hitsMs === null && hitTerm === t.term) t.hitsMs = Date.now() - t.startedAt;
    reportSearchTiming(true);
  }, [hitTerm]);

  /**
   * ONE SEARCH IS ONE VISIT TO THE SEARCH BAR (AI-3011).
   *
   * Fired on leaving it — focus out, a result opened, or the panel going away —
   * carrying the query the employer settled on rather than every prefix typed
   * on the way there. `channel_searched` above counts something else and keeps
   * counting it: one per settled query, which is a latency sample (AI-2988) and
   * a count of typing pauses. "Ahmad" typed with a hesitation settles twice and
   * typed in one burst settles once, so that number moves with typing speed
   * rather than intent and cannot answer "how many searches a day".
   *
   * Per-day and per-employer-per-day need nothing more: the BFF stamps identity
   * from the verified session and every event is timestamped, so both are
   * aggregations of this one event.
   *
   * THE QUERY TRAVELS RAW. Employers search candidate names, so this writes
   * them to PostHog — decided deliberately and recorded on AI-3011 (17 Sep),
   * because the keyword question is the whole point of the event.
   */
  const searchSnapshot = useRef({ query: '', chats: 0, messages: 0, candidates: 0 });
  searchSnapshot.current = {
    query: search.trim(),
    chats: countChannels(rows),
    messages: hitRows.length,
    candidates: countChannels(candidateRows),
  };
  // Re-armed on focus rather than on the query changing: a visit is the unit,
  // so coming back to the bar and leaving it again is a second search even if
  // nothing was retyped.
  const searchArmed = useRef(true);
  const endSearch = useCallback(() => {
    const { query, chats, messages, candidates } = searchSnapshot.current;
    if (query === '' || !searchArmed.current) return;
    searchArmed.current = false;
    track('search_performed', {
      query,
      results_chats: chats,
      results_messages: messages,
      results_jobseeker_details: candidates,
    });
  }, []);
  // Leaving the panel is leaving the search bar — on mobile, opening a chat is
  // exactly that. Guarded, so an open that already reported does not report
  // twice on the unmount it causes.
  useEffect(() => () => endSearch(), [endSearch]);

  // stream-chat parity: emit `channel-list-loaded` once, when the inbox first
  // has channels. Lives here rather than in ChatView because the inbox is the
  // only thing that still holds a list of them.
  const listLoadedRef = useRef(false);
  useEffect(() => {
    if (listLoadedRef.current || list.items.length === 0) return;
    listLoadedRef.current = true;
    emitToHost('channel-list-loaded', {
      channels: list.items.map((item) => {
        const { channelType, channelId } = cidParts(item.channel.id);
        return {
          id: channelId,
          type: channelType,
          name: item.counterpart?.name ?? '',
          memberCount: item.channel.memberIds?.length ?? 0,
        };
      }),
      count: list.items.length,
    });
    track('channel_list_viewed', { count: list.items.length });
  }, [list.items]);

  const panelJobTree = inListDetail ? listJobs : jobTree;
  const panelLocations = inListDetail ? listLocs : locationFacets.facets;

  /**
   * How many values each row of the funnel holds, named as the dialog's rows.
   * One source for the dot, the offer guard and every report — written out
   * separately they drifted, all three still describing the two-row funnel this
   * dialog replaced.
   *
   * A saved list cuts on job and location alone, so the candidate rows are not
   * counted there: they cut nothing, and a lit dot over an untouched list is
   * the failure.
   */
  const pickCounts: Record<string, number> = inListDetail
    ? { job: jobIds.length, location: listSelectedLocs.length }
    : {
        job: jobIds.length,
        location: preferredLocationPicks.length,
        language: languagePicks.length,
        skills: skillPicks.length,
        education: educationPicks.length,
        nationality: nationalityPicks.length,
        availability: availabilityPicks.length,
        gender: genderPicks.length,
        stage: stagePicks.length,
        salary: salaryRange.min === undefined && salaryRange.max === undefined ? 0 : 1,
        age: ageCut.min === undefined && ageCut.max === undefined ? 0 : 1,
      };
  const appliedFacets = Object.entries(pickCounts)
    .filter(([, values]) => values > 0)
    .map(([row]) => row);

  // A dot on each tool button, both saying the same thing: this is not the view
  // you would get fresh. The funnel's counts only what hides rows; the view
  // button's counts how the rest is drawn. Split because a dot is a thing to
  // explain, and "some chats are not on screen" and "the rows look different"
  // are not the same explanation.
  const filterActive = appliedFacets.length > 0;

  /**
   * Whether the funnel has anything to say. Only a saved list can answer no:
   * it cuts on job and location alone, so empty facets there really do open an
   * empty sheet. Everywhere else the six candidate rows are always on offer —
   * asking the job rows took the button off Direct Messages entirely.
   */
  const hasFilterSections =
    !inListDetail || panelJobTree.length > 0 || panelLocations.length > 0 || filterActive;

  // In a saved list a search matching nothing empties the facets under an open
  // funnel, and the button goes with them. Drop what it opened too: a sheet
  // outliving its button would swing back open when the facets return.
  useEffect(() => {
    if (!hasFilterSections) setTool((open) => (open === 'filter' ? null : open));
  }, [hasFilterSections]);

  // Read here rather than inside the panel, because two things need the same
  // answer: whether to offer Compact at all, and whether a Compact that cannot
  // bite should light the dot. Asked in one place they cannot disagree, and a
  // lit dot over a list that looks untouched is the failure.
  const compactable = useCanHover();
  const viewActive = groupBy !== DEFAULT_GROUP_BY || (compactable && compact !== DEFAULT_COMPACT);

  // ── funnel analytics (AI-2858) ─────────────────────────────────────────────
  /**
   * The same list with the funnel's own picks lifted — the "of 138" beside the
   * count. A result count alone says nothing: 12 of 138 is the funnel doing
   * real work, 12 of 14 is it doing almost none.
   *
   * Asked only while the panel is open, because that is the only time a pick
   * can be made, and both numbers are only ever reported together.
   */
  const unfunnelledQuery = useMemo(
    () => scopeQuery(withoutFilter(withoutFilter(query, 'job'), 'jobLocation'), 'all'),
    [query],
  );
  const unfunnelledTotal = useInboxTotal(
    unfunnelledQuery,
    (tool === 'filter' || filterActive) && !inListDetail,
    revision,
  );

  /**
   * The "of 138" itself.
   *
   * Asked while the funnel holds a pick and not merely while its panel is open,
   * because the count lands after the click and the panel is routinely gone by
   * then — a row clicked from the narrowed list, Escape, a view switch. With
   * nothing picked the two queries are the same query, so the funnelled count
   * already is the unfunnelled total and there is nothing to ask.
   *
   * A saved list counts the members it holds rather than the read model, so its
   * total is the members it was given.
   */
  const totalCount = inListDetail
    ? baseScoped.loading
      ? null
      : baseScoped.items.length
    : filterActive
      ? unfunnelledTotal
      : counts.all;

  /**
   * Which surface the funnel is being worked on. A saved list's detail runs its
   * own client-side cut over hand-picked members, and without this it is
   * indistinguishable from the inbox — same event names, same `view` — quietly
   * mixing a second funnel into the one this ticket exists to measure.
   */
  const funnelSurface = { view, in_list: inListDetail };

  /**
   * An apply, in two steps: the employer stops picking, then the counts for what
   * they settled on arrive.
   *
   * `pickIntent` is what separates an apply from any other move of the picks.
   * Three things reshape them without the funnel being touched — a view switch
   * clears them, and the derived `locations` prunes them when async facets land
   * or a job toggle reshapes the offer — and only a touched control arms this.
   * Every row is in the key: keyed on job and location alone, a language or a
   * skill settled to the same string and the apply was never reported at all.
   * It also means the mount reports nothing, which a "skip the first pass" guard
   * could not: StrictMode runs mount effects twice against the same refs.
   *
   * The wait then collapses a burst of ticks into the one state they settle on,
   * and the counts are read after it rather than during: a count is a round
   * trip, so at click time the only number in hand is the one the *previous*
   * picks produced, and for a few hundred milliseconds after that there is no
   * number at all. `useInboxTotal` answers only for the query it was asked, so
   * a non-null pair here is the count for the picks now in force.
   */
  const pickIntent = useRef(false);
  const pickKey = JSON.stringify([
    jobIds,
    locationPicks,
    preferredLocationPicks,
    languagePicks,
    skillPicks,
    educationPicks,
    nationalityPicks,
    availabilityPicks,
    genderPicks,
    stagePicks,
    salaryRange,
    ageCut,
  ]);
  const [applyKey, setApplyKey] = useState<string | null>(null);

  useEffect(() => {
    if (!pickIntent.current) return;
    const timer = setTimeout(() => setApplyKey(pickKey), PICK_QUIET_MS);
    return () => clearTimeout(timer);
  }, [pickKey]);

  useEffect(() => {
    if (applyKey !== pickKey) return;
    if (counts.all === null || totalCount === null) return;
    pickIntent.current = false;
    setApplyKey(null);
    track('filter_applied', {
      results_count: counts.all,
      total_count: totalCount,
      facets_applied: appliedFacets,
      values_count: pickCounts,
      ...funnelSurface,
    });
  }, [applyKey, pickKey, counts.all, totalCount]);

  /**
   * That the funnel was offered at all, once per inbox.
   *
   * The denominator for every rate above it. In a saved list
   * `hasFilterSections` is false until the facets land, so this cannot ride on
   * `channel_list_viewed` — that fires on the first page of rows, which
   * routinely beats the facets, and an employer who was offered a filter would
   * be counted as one who never saw one. Fired when the button appears instead.
   */
  const offerReported = useRef(false);
  useEffect(() => {
    if (offerReported.current || !hasFilterSections) return;
    offerReported.current = true;
    track('filter_offered', { ...funnelSurface });
  }, [hasFilterSections]);

  /** Whether anything was picked since the panel opened — see `closeTool`. */
  const pickedWhileOpen = useRef(false);

  const openTool = (key: Tool) => {
    if (tool === key) {
      closeTool();
      return;
    }
    // Swapping straight to the other sheet closes this one. An employer who
    // opens the funnel, cannot see what to do with it and reaches for View
    // instead has abandoned it just as surely as one who clicked away.
    reportAbandon();
    if (key === 'filter') {
      pickedWhileOpen.current = false;
      track('filter_opened', { ...funnelSurface, already_applied: appliedFacets });
    }
    setTool(key);
  };

  /**
   * A funnel opened and closed with nothing got out of it — the discoverability
   * failure this whole ticket exists to measure, reported rather than inferred
   * from the absence of a `filter_applied`.
   *
   * Abandonment needs both halves: nothing picked while it was open, AND nothing
   * already in force. Without the second, an employer who filtered a minute ago,
   * opened the panel to see what was set, and shut it again is filed under the
   * same heading as one who could not work out what the button did — and the
   * number is then mostly made of people using the filter well.
   *
   * Called from every close the employer drives: this button again, a click
   * outside, a swap to the View sheet, a view switch. Deliberately not from the
   * one close they did not drive — the panel shuts itself when the facets empty
   * under it (see `hasFilterSections` above), and counting that would blame them
   * for a search that matched nothing.
   */
  const reportAbandon = () => {
    if (tool === 'filter' && !pickedWhileOpen.current && !filterActive) {
      track('filter_abandoned', { ...funnelSurface });
    }
  };

  const closeTool = () => {
    reportAbandon();
    setTool(null);
  };

  /**
   * Every row click on the inbox, carrying what the funnel was set to when it
   * happened.
   *
   * Repeated on each open rather than joined back to `filter_applied` by
   * session: one visit changes the funnel several times and opens several
   * chats, and stitching those together by timestamp is both fragile and easy
   * to get quietly wrong. Each open says for itself what it was opened from.
   */
  const selectFromInbox = (item: InboxItem, messageId?: string) => {
    // Opening a result ends the search. Before `onSelect`, so the search that
    // produced the open is reported ahead of the open itself.
    endSearch();
    // Whether a particular message was named on the way in. Set on every open,
    // null included, so a chat opened the ordinary way after one opened from a
    // search does not inherit the last landing.
    setOpenedHit(messageId ?? null);
    onSelect(
      item,
      {
        filter_active: filterActive,
        search_active: search.trim() !== '',
        // A message row names its message; a Jobseeker Details row is one the
        // split put there. Everything else is a chat row, on or off a search.
        result_list: messageId
          ? 'messages'
          : candidateChannelIds.has(item.channel.id)
            ? 'jobseeker_details'
            : 'chats',
        facets_applied: appliedFacets,
        values_count: pickCounts,
        // The rows on screen are held through a recount, so the held count is what
        // was actually in front of them; `counts.all` alone is null for the gap
        // right after a pick, which is the most interesting moment in this funnel.
        results_count: counts.all ?? heldAll,
      },
      messageId,
    );
  };

  const toggleJob = (id: string) => {
    pickedWhileOpen.current = true;
    pickIntent.current = true;
    setJobIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  };

  // A set union, not a concatenation: under a search the ids handed over
  // overlap what is already picked, and a duplicate makes a count read double.
  const selectManyJobs = (ids: string[], on: boolean) => {
    pickedWhileOpen.current = true;
    pickIntent.current = true;
    setJobIds((current) =>
      on ? [...new Set([...current, ...ids])] : current.filter((held) => !ids.includes(held)),
    );
  };

  /** The tick rows toggle alike; only the list they hold differs. */
  const togglePick =
    <T extends string>(set: Dispatch<SetStateAction<T[]>>) =>
    (key: T) => {
      pickedWhileOpen.current = true;
      pickIntent.current = true;
      set((current) =>
        current.includes(key) ? current.filter((value) => value !== key) : [...current, key],
      );
    };

  const changeRange = (set: Dispatch<SetStateAction<NumberRange>>) => (range: NumberRange) => {
    pickedWhileOpen.current = true;
    pickIntent.current = true;
    set(range);
  };

  /** Everything the funnel sets, put down at once. */
  const clearCandidatePicks = () => {
    setSkillPicks([]);
    setLanguagePicks([]);
    setEducationPicks([]);
    setPreferredLocationPicks([]);
    setNationalityPicks([]);
    setAvailabilityPicks([]);
    setGenderPicks([]);
    setStagePicks([]);
    setSalaryRange({});
    setAgeRange({});
  };

  const changeView = (next: InboxView) => {
    reportAbandon();
    setView(next);
    setTool(null);
    // A view switch is a fresh context, and its funnel offers whatever that
    // view's facets hold. Carrying picks across would leave one narrowing a
    // list whose panel no longer shows the control that set it.
    setJobIds([]);
    setLocationPicks([]);
    clearCandidatePicks();
    setGroupBy(DEFAULT_GROUP_BY);
  };

  /**
   * The header's two tools, in the order their questions are asked: how the
   * list is drawn, then how much of it there is. One descriptor each, so the
   * buttons are drawn from one shape rather than written twice, and the sheet
   * rides along with the button that opens it: a tool with no button cannot
   * leave its panel on screen.
   */
  const tools: {
    key: Tool;
    icon: IconName;
    title: string;
    active: boolean;
    panel: ReactNode;
  }[] = [
    {
      key: 'view',
      icon: 'layoutList',
      title: 'View',
      active: viewActive,
      panel: (
        <ViewPanel
          groupBy={groupBy}
          onGroupBy={(next) => {
            // The segment reports every press, the already-active one included.
            if (next !== groupBy) track('group_by_changed', { group_by: next, ...funnelSurface });
            setGroupBy(next);
          }}
          canGroupByJob={groupByJobOffered}
          compact={compact}
          onCompact={setCompact}
          compactable={compactable}
        />
      ),
    },
    ...(hasFilterSections
      ? [
          {
            key: 'filter' as const,
            icon: 'filter' as const,
            title: 'Filter',
            active: filterActive,
            // The funnel is a dialog now, not a sheet in this cluster: it draws
            // its own scrim and owns its own dismissal, and a modal rendered
            // into the popover slot beside the View sheet would sit under the
            // header it is supposed to cover. It renders at the panel root
            // instead — see below.
            panel: null,
          },
        ]
      : []),
  ];

  return (
    <div className={compact && compactable ? `${styles.panel} ${styles.boxesAway}` : styles.panel}>
      {/* SELECTION MODE takes this over: the title becomes the count, the All
          pill becomes select-all, the tools become the actions. Every row keeps
          its height, so the first tick moves nothing below. The ✕ is a cancel. */}
      <div className={styles.header} data-selecting={selecting}>
        <div className={styles.titleRow}>
          {selecting ? (
            <div className={styles.selectTitle}>
              <button
                type="button"
                className={styles.selectClose}
                onClick={clearSelection}
                title="Clear selection"
                aria-label="Clear selection"
              >
                <Icon name="close" size={20} />
              </button>
              <h1 className={styles.selectCount}>{selectedItems.length} Selected</h1>
            </div>
          ) : inListDetail ? (
            // The list is a place you're in, not a filter the inbox wears: its
            // badge leads its name as the title, the count sits under it — the
            // list is the hero. Matches the mock's SavedListPanel.
            <div className={styles.listHead}>
              <button
                className={styles.back}
                onClick={onExitList}
                title="Back to lists"
                aria-label="Back to lists"
              >
                {/* chevronRight flipped into a left arrow. */}
                <span className={styles.backArrow}>
                  <Icon name="chevronRight" size={18} />
                </span>
              </button>
              {activeList && <ListBadge list={activeList} />}
              <div className={styles.heading}>
                <span className={styles.listName}>{activeList?.name ?? 'Saved List'}</span>
                {activeList &&
                  ((listMembers.loading || listMembers.error) && listMembers.items.length === 0 ? (
                    // Skeleton on the first load (and on a first-load error, where
                    // the body shows the retry) — so the subtitle never flashes
                    // "0 of N" before the members resolve.
                    <span className={styles.summarySkeleton} aria-hidden="true" />
                  ) : (
                    <span className={styles.listSummary}>
                      {/* Total = every id saved to the list (mock parity with
                          SavedListPanel: channelIds.length). An unresolved or
                          ended member is still counted, so removing a member
                          drops the total. "Still open" = resolved, not ended,
                          AND still in the live stored set — intersecting with
                          activeChannelSet (which shrinks synchronously on remove)
                          keeps open from briefly outrunning the total, e.g.
                          "3 of 2", until the Stream re-query catches up. */}
                      {listSummary(
                        activeList.channelIds.length,
                        listMembers.items.filter(
                          (item) => activeChannelSet.has(item.channel.id) && !isEnded(item.channel),
                        ).length,
                      )}
                    </span>
                  ))}
              </div>
            </div>
          ) : (
            <>
              <KindStepper view={view} onChange={changeView} />
              {/* In every view, not just Job Messages. What the "+" opens is the
                  host's own picker, and starting a conversation is not a property
                  of the cut you happen to be looking at — a button that came and
                  went as you stepped through the views said it was, and left the
                  two other views looking like dead ends. The slot is also what
                  keeps the chevrons from shifting as the title changes width. */}
              {onNewChat && (
                <div className={styles.titleAction}>
                  <button className={styles.newChat} onClick={onNewChat} title="Start new chat">
                    <Icon name="plus" size={20} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.search}>
          <span className={styles.searchIcon}>
            <Icon name="search" size={17} />
          </span>
          <input
            className={styles.searchInput}
            placeholder={inListDetail ? 'Search this list' : 'Search your conversation'}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onFocus={() => {
              searchArmed.current = true;
            }}
            onBlur={endSearch}
          />
        </div>

        {/* A list detail searches its loaded members client-side, so it never
            asks Stream and can never be the half-answer this reports. */}
        {!inListDetail && list.searchDegraded && (
          <p className={styles.searchNotice} role="status">
            Couldn&apos;t search messages. Name matches only.
          </p>
        )}

        {/* Said rather than left as a list that simply stops: the count above
            it goes on naming rows this walk can no longer reach. */}
        {!inListDetail && list.searchExpired && (
          <p className={styles.searchNotice} role="status">
            This search timed out. Search again to see the rest.
          </p>
        )}

        {/* The row narrows the list, and acts on it while a selection is up. */}
        <div className={styles.filters}>
          {/* Its own slot: this is the pill that changes shape. */}
          <div className={styles.scopeSlot}>
            {selecting ? (
              /* An OFFER, not a report, since the title already counts the
                 picks: it carries how far "all" reaches.
                 A label, not a button: <input> inside <button> is invalid. */
              <label className={styles.selectAll}>
                <span className={styles.selectAllBox}>
                  {/* Empty until every visible row is picked, never dashed: a
                      dash reports where the selection stands, which the title
                      already does. No `label` prop: the text beside it is the
                      box's visible label, and an aria-label would override it. */}
                  <Checkbox checked={allVisibleSelected} onChange={toggleSelectAll} />
                </span>
                <span className={styles.selectAllLabel}>
                  {/* Says what the press does. Ticked, the count goes with the
                      label: there is no offer left to measure, and the number
                      would only repeat the title's. */}
                  {allVisibleSelected ? (
                    'Unselect all'
                  ) : (
                    <>
                      Select all
                      <PillCount value={visibleIds.length} />
                    </>
                  )}
                </span>
              </label>
            ) : (
              <button
                className={!unread ? styles.filterPillActive : styles.filterPill}
                onClick={() => setUnread(false)}
              >
                All
                <PillCount value={counts.all} />
              </button>
            )}
          </div>
          <div className={styles.scopes}>
            <button
              className={unread ? styles.filterPillActive : styles.filterPill}
              onClick={() => setUnread(true)}
            >
              Unread
              <PillCount value={counts.unread} />
            </button>
          </div>

          {/* One slot, taking turns, so the actions land where the eye already
              is. The tools go rather than grey out: they ask about a list nobody
              is reading while a set is worked through. */}
          {selecting ? (
            <div className={styles.actionSlot} key="actions">
              <BulkActions
                targets={targets}
                rejectReach={rejectReach}
                onSend={() => setComposeOpen(true)}
                onAction={(action) => {
                  // Message is the primary's own handler; the overflow only ever
                  // fires the stages and End, which is what this dialog covers.
                  if (action !== 'message') setBulkAction(action);
                }}
                onAddToList={() => setAddingToList(true)}
                listName={activeList?.name ?? null}
                onRemoveFromList={handleRemoveFromList}
              />
            </div>
          ) : (
            /* Two questions, two buttons. Both sheets open against the cluster's
               right edge, so only one is ever out (see `Tool`). The funnel
               narrows through the read model here and client-side in a saved
               list: same panel, options sourced per case. */
            <div className={styles.tools} ref={toolsRef} key="tools">
              {tools.map((entry) => (
                <button
                  key={entry.key}
                  type="button"
                  className={
                    tool === entry.key || entry.active ? styles.toolBtnActive : styles.toolBtn
                  }
                  title={entry.title}
                  aria-haspopup="dialog"
                  aria-expanded={tool === entry.key}
                  onClick={() => openTool(entry.key)}
                >
                  <Icon name={entry.icon} size={17} />
                  {entry.active && <span className={styles.toolDot} />}
                </button>
              ))}
              {tools.find((entry) => entry.key === tool)?.panel}
            </div>
          )}
        </div>
      </div>

      {tool === 'filter' && hasFilterSections && (
        <FilterDialog
          /* A saved list is not view-scoped: its members can be job chats
             whatever the stepper last showed. */
          jobRows={inListDetail || canGroupByJob(view)}
          jobTree={panelJobTree}
          selectedJobIds={jobIds}
          onToggleJob={toggleJob}
          onSelectManyJobs={selectManyJobs}
          stage={{
            options: stageOptions,
            selected: stagePicks,
            onToggle: togglePick(setStagePicks),
          }}
          preferredLocation={{
            options: preferredLocationFacets.facets,
            selected: preferredLocationPicks,
            onToggle: togglePick(setPreferredLocationPicks),
            ...(preferredLocationFacets.unknownCount === undefined
              ? {}
              : { unknownCount: preferredLocationFacets.unknownCount }),
          }}
          language={{
            options: languageFacets.facets,
            selected: languagePicks,
            onToggle: togglePick(setLanguagePicks),
            ...(languageFacets.unknownCount === undefined
              ? {}
              : { unknownCount: languageFacets.unknownCount }),
          }}
          skills={{
            options: skillFacets.facets,
            selected: skillPicks,
            onToggle: togglePick(setSkillPicks),
            ...(skillFacets.unknownCount === undefined
              ? {}
              : { unknownCount: skillFacets.unknownCount }),
          }}
          education={{
            options: educationFacets.facets,
            selected: educationPicks,
            onToggle: togglePick(setEducationPicks),
            ...(educationFacets.unknownCount === undefined
              ? {}
              : { unknownCount: educationFacets.unknownCount }),
          }}
          nationality={{
            options: nationalityFacets.facets,
            selected: nationalityPicks,
            onToggle: togglePick(setNationalityPicks),
            ...(nationalityFacets.unknownCount === undefined
              ? {}
              : { unknownCount: nationalityFacets.unknownCount }),
          }}
          availability={{
            options: availabilityFacets.facets,
            selected: availabilityPicks,
            onToggle: togglePick(setAvailabilityPicks),
            ...(availabilityFacets.unknownCount === undefined
              ? {}
              : { unknownCount: availabilityFacets.unknownCount }),
          }}
          gender={{
            options: genderFacets.facets,
            selected: genderPicks,
            onToggle: togglePick(setGenderPicks),
            ...(genderFacets.unknownCount === undefined
              ? {}
              : { unknownCount: genderFacets.unknownCount }),
          }}
          salary={{ range: salaryRange, onChange: changeRange(setSalaryRange) }}
          age={{ range: ageRange, onChange: changeRange(setAgeRange) }}
          /* One reset for the dialog, where the popover had one per section.
             Its scope is the whole funnel and it sits in the footer beside the
             number it changes, so a per-row Clear would be the only one of the
             two whose reach you could not see. */
          onClearAll={() => {
            pickedWhileOpen.current = true;
            pickIntent.current = true;
            track('filter_cleared', { facet: 'all', ...funnelSurface });
            setJobIds([]);
            setLocationPicks([]);
            clearCandidatePicks();
          }}
          /* The same number `filter_applied` reports, so the dialog and the
             analytics can never disagree about how far the funnel cut. */
          results={{ shown: counts.all ?? heldAll }}
          onClose={closeTool}
        />
      )}

      {composeOpen && (
        <BulkSendDialog
          targets={selectedItems}
          onSend={handleBulkSend}
          onClose={(sent) => {
            setComposeOpen(false);
            if (sent) clearSelection();
          }}
        />
      )}

      {bulkAction && (
        <BulkActionDialog
          action={bulkAction}
          targets={selectedItems.filter((item) => targets[bulkAction].includes(item.channel.id))}
          skipped={selectedOnScreenIds.length - targets[bulkAction].length}
          rejects={rejectReach}
          onConfirm={(text, onProgress) => runBulkAction(bulkAction, text, onProgress)}
          onClose={(done) => {
            const wasEnd = bulkAction === 'end';
            setBulkAction(null);
            if (done) {
              clearSelection();
              // Ended chats leave the open inbox — re-read so they drop from the
              // list now, not on a later event. In a list detail the rows come from
              // useListMembers (keyed on the stored ids, which End doesn't change),
              // so refresh it too or the ended members keep their stale open styling.
              if (wasEnd) {
                list.reload();
                listMembers.reload();
              }
            }
          }}
        />
      )}

      {addingToList && (
        <AddToListDialog
          targets={selectedItems}
          onApplied={(list, direction) => {
            const affected = [...selectedOnScreenIds];
            // Before `clearSelection`, which resets the flag.
            if (direction === 'added') {
              track('bulk_list_added', {
                action: 'add_to_list',
                targeted: affected.length,
                select_all: selectedViaAll.current,
              });
            }
            setAddingToList(false);
            clearSelection();
            // Unticking a list in the picker is the same removal the bulk menu
            // runs, reached another way — so it ends the same way, Undo and all.
            if (direction === 'removed') {
              setToast(removedReport(list, affected, addTo));
              return;
            }
            // Filing shows where the chats went, not an Undo: re-filing IS the
            // undo, and offering both would dress the safe thing as risky.
            // "View list" is the trip, offered when the panel can make it.
            setToast({
              title: `Added to ${list.name}`,
              detail: plural(affected.length, 'conversation'),
              action: onOpenList
                ? { label: 'View list', onClick: () => onOpenList(list.id) }
                : undefined,
            });
          }}
          onClose={() => setAddingToList(false)}
        />
      )}

      <InboxRows
        list={scopedList}
        rows={rows}
        candidateRows={candidateRows}
        grouped={activeList ? false : grouped}
        clientGroups={listGroups}
        facets={groupFacets}
        query={query}
        client={client}
        selectedId={selectedId}
        onSelect={selectFromInbox}
        selectedSet={selectedSet}
        selecting={selecting}
        onToggleSelect={toggleSelect}
        onSelectMany={selectMany}
        expandedPeople={expandedSet}
        onSectionRows={reportSectionRows}
        onToggleExpand={toggleExpand}
        // The search's two groups. A saved list's detail keeps its own scope:
        // hits are account-wide, and rows from outside the list would read as
        // members it never had.
        search={
          activeListId === null && search.trim() !== ''
            ? { term: debounced, hitTerm, hitRows, pending: hitTerm !== search.trim() }
            : null
        }
        openedHit={openedRow}
      />

      {toast && (
        <div className={styles.toastSlot}>
          <Toast
            title={toast.title}
            detail={toast.detail}
            action={toast.action}
            onClose={() => setToast(null)}
          />
        </div>
      )}
    </div>
  );
}

/** What the toast says once a filing has run, and what it offers afterwards. */
interface ToastState {
  title: string;
  detail?: string;
  action?: ToastAction;
}

/**
 * What a removal from a list says, and how to take it back. Written once because
 * two paths run the same removal — the bulk menu's row and unticking a list in
 * the picker — and a card that offers Undo on one and not the other is a
 * difference nobody can account for later. Re-filing is a normal errand, so the
 * Undo is a shortcut, not the only way back.
 */
function removedReport(
  list: ConversationList,
  channelIds: ChannelId[],
  addTo: UseLists['addTo'],
): ToastState {
  return {
    title: `Removed from ${list.name}`,
    detail: plural(channelIds.length, 'conversation'),
    action: { label: 'Undo', onClick: () => addTo(list.id, channelIds) },
  };
}

interface Facet {
  key: string;
  label: string;
  count: number;
}

interface InboxRowsProps {
  list: UseInboxList;
  /** `list.items` with each person's several applications folded (see `stackRows`). */
  rows: InboxRow[];
  grouped: boolean;
  /** A saved list's own client-side groups; null outside a grouped list detail. */
  clientGroups: ListGroup[] | null;
  facets: Facet[];
  query: InboxQuery;
  client: StreamChat | null;
  selectedId: ChannelId | null;
  onSelect: (item: InboxItem, messageId?: string) => void;
  selectedSet: Set<ChannelId>;
  selecting: boolean;
  onToggleSelect: (id: ChannelId) => void;
  onSelectMany: (ids: ChannelId[], selected: boolean) => void;
  /** The people whose folded rows are open, by participant id. */
  expandedPeople: Set<ParticipantId>;
  onToggleExpand: (id: ParticipantId) => void;
  /**
   * A search is on, so the list answers in groups: Chats are the conversations
   * whose own name matched, Messages the individual messages that did — one
   * row each, words picked out. Which group a row is in IS the explanation.
   * `term` marks the chat rows; `hitTerm` is the term the hits actually
   * answer, which lags the box; `pending` holds the no-matches verdict back
   * until the hits have actually been looked for.
   */
  /** Where a section hands its loaded rows up: only it queried them (AI-3006). */
  onSectionRows: (key: string, items: InboxItem[]) => void;
  search: { term: string; hitTerm: string; hitRows: HitRow[]; pending: boolean } | null;
  /**
   * The Jobseeker Details group: chats the search matched only through the
   * candidate's profile, split off by the panel (see the split's comment).
   * Flat inbox only — the server-grouped Job funnel and a saved list's detail
   * never populate it.
   */
  candidateRows: InboxRow[];
  /** The message row that was opened, while it is still on screen. */
  openedHit: string | null;
}

function InboxRows({
  list,
  rows,
  candidateRows,
  grouped,
  clientGroups,
  facets,
  query,
  client,
  selectedId,
  onSelect,
  selectedSet,
  selecting,
  onToggleSelect,
  onSelectMany,
  onSectionRows,
  expandedPeople,
  onToggleExpand,
  search,
  openedHit,
}: InboxRowsProps) {
  const sentinel = useRef<HTMLDivElement>(null);

  // Infinite scroll via an observer rather than a scroll handler, so it costs
  // nothing while the sentinel is off screen.
  useEffect(() => {
    const node = sentinel.current;
    if (!node || grouped) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) list.loadMore();
    });
    io.observe(node);
    return () => io.disconnect();
  }, [list, grouped]);

  if (list.loading) {
    return (
      <div className={styles.list}>
        <LoadingState label="Fetching your messages" />
      </div>
    );
  }

  // A failed query must never read as "you have no conversations".
  if (list.error) {
    return (
      <div className={styles.list}>
        <EmptyState
          title="Couldn't load conversations"
          description="Something went wrong reaching the server."
        />
        <button className={styles.retry} onClick={list.reload}>
          Try again
        </button>
      </div>
    );
  }

  // A saved list groups its loaded members client-side (its funnel is not the
  // read model's — see InboxPanel's scope block), so it draws its own groups
  // rather than the lazy, server-scoped GroupSection.
  if (clientGroups) {
    return (
      <div className={styles.list}>
        {clientGroups.length === 0 ? (
          <EmptyState title="No conversations" description="Nothing matches your filters." />
        ) : (
          clientGroups.map((group) => (
            <div key={group.key} className={styles.group}>
              <div className={styles.clientGroupHeader}>
                <GroupHead groupKey={group.key} label={group.label} count={group.items.length} />
              </div>
              {group.items.map((item) => (
                <ChannelPreview
                  key={item.channel.id}
                  item={item}
                  active={item.channel.id === selectedId}
                  onClick={() => onSelect(item)}
                  selectable
                  selected={selectedSet.has(item.channel.id)}
                  selecting={selecting}
                  onToggleSelect={() => onToggleSelect(item.channel.id)}
                />
              ))}
            </div>
          ))
        )}
      </div>
    );
  }

  // A chat row must not stay lit while a message row is the thing that was
  // opened — both name the same channel, and lighting both would leave nothing
  // saying which was pressed.
  const chatSelectedId = openedHit !== null ? null : selectedId;

  /**
   * The Messages group: one row per matching message, dated by it, words
   * picked out. The same conversation row, not a second component — it keeps
   * the face, the name, the unread weight and the checkbox (which still picks
   * the chat; there is nothing bulk-sendable about a message), because it IS
   * that conversation, seen through a different question. Keyed by message,
   * since one chat can answer a search several times.
   */
  const messagesGroup =
    search && search.hitRows.length > 0 ? (
      <>
        <div className={styles.searchGroupHead}>Messages</div>
        {search.hitRows.map(({ hit, item, snippet }) => (
          <ChannelPreview
            key={hit.messageId}
            item={item}
            term={search.hitTerm}
            message={{
              preview: <Marked text={snippet} term={search.hitTerm} />,
              at: hit.createdAt,
            }}
            active={hit.messageId === openedHit}
            onClick={() => onSelect(item, hit.messageId)}
            selectable
            selected={selectedSet.has(item.channel.id)}
            selecting={selecting}
            onToggleSelect={() => onToggleSelect(item.channel.id)}
          />
        ))}
      </>
    ) : null;

  /* Held back while the hits are still being looked for: the chat list answers
     instantly from the read model, the hits a round trip behind, and a verdict
     drawn in that gap says "no matches" about a search that has not run. */
  const noMatches = search && !search.pending && search.hitRows.length === 0 && (
    <EmptyState title="No matches" description={`Nothing matches "${search.term}".`} />
  );

  if (grouped) {
    return (
      <div className={styles.list}>
        {facets.length === 0 ? (
          (messagesGroup ??
          (search ? (
            noMatches
          ) : (
            <EmptyState title="No conversations" description="Nothing matches your filters." />
          )))
        ) : (
          <>
            {search && <div className={styles.searchGroupHead}>Chats</div>}
            {facets.map((facet) => (
              <GroupSection
                key={facet.key}
                facet={facet}
                query={query}
                client={client}
                selectedId={chatSelectedId}
                onSelect={onSelect}
                selectedSet={selectedSet}
                selecting={selecting}
                onToggleSelect={onToggleSelect}
                onRows={onSectionRows}
              />
            ))}
            {messagesGroup}
          </>
        )}
      </div>
    );
  }

  if (!search && list.items.length === 0) {
    return (
      <div className={styles.list}>
        <EmptyState title="No conversations" description="Nothing matches your filters." />
      </div>
    );
  }

  const renderRow = (row: InboxRow) =>
    row.kind === 'stack' ? (
      <StackedPreview
        key={row.key}
        counterpart={row.counterpart}
        items={row.items}
        selectedId={chatSelectedId}
        expanded={expandedPeople.has(row.counterpart.id)}
        onToggleExpand={() => onToggleExpand(row.counterpart.id)}
        onSelect={onSelect}
        selectable
        selectedIds={selectedSet}
        selecting={selecting}
        onSelectMany={onSelectMany}
      />
    ) : (
      <ChannelPreview
        key={row.key}
        item={row.item}
        term={search?.term ?? ''}
        active={row.item.channel.id === chatSelectedId}
        onClick={() => onSelect(row.item)}
        selectable
        selected={selectedSet.has(row.item.channel.id)}
        selecting={selecting}
        onToggleSelect={() => onToggleSelect(row.item.channel.id)}
      />
    );

  return (
    <div className={styles.list}>
      {search && rows.length > 0 && <div className={styles.searchGroupHead}>Chats</div>}
      {rows.map(renderRow)}
      {messagesGroup}
      {/* The same conversation row under a different header — the header IS
          the explanation, so the row itself carries nothing extra. Fixed
          order (Chats, Messages, Jobseeker Details), by kind and never by
          size: message hits are direct evidence of the words typed, profile
          matches an inference from them — certainty sorts first, and a common
          skill can profile-match enough people to bury anything below it. */}
      {candidateRows.length > 0 && <div className={styles.searchGroupHead}>Jobseeker Details</div>}
      {candidateRows.map(renderRow)}
      {rows.length === 0 && candidateRows.length === 0 && noMatches}
      {list.hasMore && (
        <div ref={sentinel} className={styles.hint}>
          {list.loadingMore ? 'Loading more…' : ''}
        </div>
      )}
    </div>
  );
}

/**
 * The count on a scope pill, or nothing at all while it is unknown.
 *
 * Shortened past a thousand (see `compactCount`) so the pill changes unit
 * rather than width, with the exact figure on hover whenever it was shortened —
 * an approximation an employer cannot resolve is worse than no number.
 */
function PillCount({ value }: { value: number | null }) {
  if (value === null) return null;
  const shown = compactCount(value);
  return (
    <span className={styles.pillCount} title={shown === String(value) ? undefined : String(value)}>
      {shown}
    </span>
  );
}

/**
 * A job section's face: the glyph, the posting's title, and how much it holds.
 *
 * Only Job sections the list now — Person folds in place — so there is no case
 * here that wants a face. What it wants is the briefcase the folded rows'
 * threads already lead with, and the bubble for the remainder of chats that
 * name no posting, so "Direct messages" is told apart from a posting called
 * something similar without reading the words.
 *
 * `plural(count, 'chat')` and no unread half, unlike the folds: the read
 * model's facets carry a label and a count and nothing else, so a number for
 * unread up here is one nothing on the wire can answer.
 */
function GroupHead({ groupKey, label, count }: { groupKey: string; label: string; count: number }) {
  return (
    <>
      <span className={styles.groupGlyph}>
        <Icon name={groupKey === DIRECT_GROUP_KEY ? 'chatBubble' : 'briefcase'} size={16} />
      </span>
      <span className={styles.groupText}>
        <span className={styles.groupName}>{label || groupKey}</span>
        <span className={styles.groupMeta}>{plural(count, 'chat')}</span>
      </span>
    </>
  );
}

interface GroupSectionProps {
  facet: Facet;
  query: InboxQuery;
  client: StreamChat | null;
  selectedId: ChannelId | null;
  onSelect: (item: InboxItem) => void;
  selectedSet: Set<ChannelId>;
  selecting: boolean;
  onToggleSelect: (id: ChannelId) => void;
  /** Where this section hands its loaded rows up (AI-3006). */
  onRows: (key: string, items: InboxItem[]) => void;
}

/**
 * One group's header and rows. Sections load lazily: a grouped view costs one
 * facets call plus a query per section the user actually opens, rather than a
 * query per group up front.
 */
function GroupSection({
  facet,
  query,
  client,
  selectedId,
  onSelect,
  selectedSet,
  selecting,
  onToggleSelect,
  onRows,
}: GroupSectionProps) {
  const [open, setOpen] = useState(false);

  // Whether scoping actually narrowed anything, read off the one call that
  // knows — rather than re-deriving it from the strategy registry, which does
  // not own the remainder section's key.
  const scoped = useMemo(() => scopedQuery(query, facet.key), [query, facet.key]);
  const scopable = scoped !== query;
  const sectionQuery = useMemo(
    () => ({ ...scoped, grouping: { by: 'none' as GroupBy } }),
    [scoped],
  );
  // Passing a null client keeps the section from querying until it is opened.
  const section = useInboxList(sectionQuery, open && scopable ? client : null);

  /**
   * Hand the rows up: the panel cannot see them, and it is what decides how far
   * a bulk action reaches (AI-3006). Closed reports nothing, since the hook
   * keeps the last rows it loaded after the client goes null.
   */
  const rows = open && scopable && !section.loading ? section.items : NO_ITEMS;
  useEffect(() => {
    onRows(facet.key, rows);
  }, [facet.key, rows, onRows]);
  // Unmount only, or switching the grouping away leaves these rows behind.
  useEffect(() => () => onRows(facet.key, NO_ITEMS), [facet.key, onRows]);

  return (
    <div className={styles.group}>
      <button
        className={styles.groupHeader}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <GroupHead groupKey={facet.key} label={facet.label} count={facet.count} />
        <span className={styles.groupChevron} aria-hidden="true">
          <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} />
        </span>
      </button>
      {open &&
        (!scopable ? (
          // No current grouping hits this — job, person and stage all scope.
          // The guard stays because the alternative failure is silent: an
          // unscoped section would render the whole inbox under one header.
          <p className={styles.hint}>Opening this group isn’t available yet.</p>
        ) : section.loading ? (
          <p className={styles.hint}>Loading…</p>
        ) : (
          section.items.map((item) => (
            <ChannelPreview
              key={item.channel.id}
              item={item}
              active={item.channel.id === selectedId}
              onClick={() => onSelect(item)}
              selectable
              selected={selectedSet.has(item.channel.id)}
              selecting={selecting}
              onToggleSelect={() => onToggleSelect(item.channel.id)}
            />
          ))
        ))}
    </div>
  );
}
